import { MentionsTextField } from "@jackstenglein/mui-mentions";
import ChatIcon from "@mui/icons-material/Chat";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import EditIcon from "@mui/icons-material/Edit";
import ReplyIcon from "@mui/icons-material/Reply";
import SendIcon from "@mui/icons-material/Send";
import {
  Avatar,
  Badge,
  Box,
  Button,
  capitalize,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fab,
  IconButton,
  Slide,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router";
import { EmptyState } from "@/components/ui";
import { useCommentsContext } from "../contexts/comments-context";
import {
  useApiMutation,
  useApiQuery,
  useCommentReadReceipts,
  useMarkAllCommentsRead,
  useMarkCommentsRead,
  useUnreadCommentsCount,
} from "../hooks";
import { useMemberInfo } from "../hooks/use-member-info";
import type { Comment, CommentEntityType } from "../types";
import { clickableProps } from "../utils/keyboard";

const COMMENTS_DRAWER_WIDTH = 360;

const memberFullName = (id: string, member?: { firstName?: string; lastName?: string }) => {
  if (!member) {
    return id;
  }

  return `${member.firstName ?? id} ${member.lastName ?? ""}`.trim();
};

const locArrToEntity = (arr: string[]): [CommentEntityType, string | null] => {
  if (arr.length === 0) {
    return ["dashboard", null];
  }

  switch (arr[0]) {
    case "tests-main":
      if (arr.length > 1) {
        let firstPart: CommentEntityType = "general";

        switch (arr[1]) {
          case "scenarios":
            firstPart = arr.length === 2 ? "scenarios" : "scenario";
            break;
          case "tests":
            firstPart = arr.length === 2 ? "tests" : "test";
            break;
          case "test-suites":
            firstPart = arr.length === 2 ? "test_suites" : "test_suite";
            break;
        }

        return [firstPart, arr?.[2] || null];
      }

      break;

    case "tests":
      if (arr.length === 1) {
        return ["tests", null];
      } else {
        return ["test", arr?.[1] || null];
      }

    case "test-suites":
      if (arr.length === 1) {
        return ["test_suite", null];
      } else {
        return ["test_suite", arr?.[1] || null];
      }

    case "runs":
      return ["test_run", arr?.[1] || null];

    case "suite-runs":
      return ["test_suite_run", arr?.[1] || null];

    case "graph":
      return ["graph", null];

    case "generate":
      return ["generate", null];

    case "configuration":
      return ["settings", null];

    default:
  }

  return ["general", null];
};

const CommentText = ({ text }: { text: string }) => {
  const { members } = useMemberInfo();

  return (
    <MentionsTextField
      dataSources={[
        {
          data: members
            ?.filter((m) => !!m)
            .map((member) => ({
              id: member.userId!,
              display: `${member.firstName} ${member.lastName}`,
            })),
        },
      ]}
      contentEditable={false}
      highlightColor="#d1e7dd"
      variant="standard"
      fullWidth
      multiline
      size="small"
      type="text"
      rows={text.length / 37}
      value={text}
      aria-readonly
      InputProps={{
        disableUnderline: true,
      }}
    />
  );
};

type CommentAddProps = {
  entityType: CommentEntityType;
  entityId?: string | null;
  threadId?: string | null;
  commentId?: string;
  snackbar?: boolean;
  label?: string;
  onSuccess?: (_: Comment) => void;
};

export const CommentAdd = (props: CommentAddProps) => {
  const { enqueueSnackbar } = useSnackbar();

  const { members } = useMemberInfo();
  const [comment, setComment] = useState<string>("");

  const isEditing = !!props.commentId;

  const { data: maybeComment, isLoading: maybeCommentLoading } = useApiQuery<Comment>(
    `/comments/${props.commentId}`,
    {
      enabled: !!props.commentId,
    },
  );

  useEffect(() => {
    if (maybeComment) {
      setComment(maybeComment.text);
    }
  }, [maybeComment]);

  const { mutate: addComment, isPending: isAddingComment } = useApiMutation<
    Pick<Comment, "entity_id" | "entity_type" | "text" | "thread_id">,
    Comment
  >("POST", "/comments", {
    onSuccess: (comment) => {
      if (props.snackbar === true) {
        enqueueSnackbar("Comment added successfully", {
          variant: "success",
          anchorOrigin: {
            horizontal: "left",
            vertical: "bottom",
          },
        });
      }

      setComment("");

      if (props.onSuccess) {
        props.onSuccess(comment);
      }
    },
  });

  const { mutate: updateComment, isPending: isUpdatingComment } = useApiMutation<
    Pick<Comment, "text">,
    Comment
  >("PUT", props.commentId ? `/comments/${props.commentId}` : "/comments", {
    onSuccess: (comment) => {
      enqueueSnackbar("Comment updated successfully", {
        variant: "success",
        anchorOrigin: {
          horizontal: "left",
          vertical: "bottom",
        },
      });

      setComment("");

      if (props.onSuccess) {
        props.onSuccess(comment);
      }
    },
  });

  return (
    <MentionsTextField
      autoFocus
      dataSources={[
        {
          data: members
            ?.filter((m) => !!m)
            .map((member) => ({
              id: member.userId!,
              display: `${member.firstName} ${member.lastName}`,
            })),
        },
      ]}
      disabled={maybeCommentLoading || isAddingComment || isUpdatingComment}
      highlightColor="#d1e7dd"
      variant="outlined"
      label={props?.label ?? (isEditing ? "Edit comment" : "Add a comment")}
      fullWidth
      multiline
      size="small"
      type="text"
      rows={3}
      sx={{ marginTop: 1 }}
      value={comment}
      onChange={(e) => setComment(e)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
          e.preventDefault();

          if (comment.trim()) {
            if (isEditing) {
              updateComment({
                text: comment,
              });
            } else {
              addComment({
                entity_type: props.entityType,
                entity_id: props.entityId ?? undefined,
                text: comment,
                thread_id: props.threadId ?? undefined,
              });
            }
          }
        }
      }}
      slotProps={{
        input: {
          endAdornment: (
            <Tooltip title="Submit comment (or ctrl + enter)">
              <IconButton
                aria-label="Submit comment (or ctrl + enter)"
                color="primary"
                disabled={isAddingComment || isUpdatingComment || !comment.trim()}
                loading={isAddingComment || isUpdatingComment}
                onClick={() => {
                  if (comment.trim()) {
                    if (isEditing) {
                      updateComment({
                        text: comment,
                      });
                    } else {
                      addComment({
                        entity_type: props.entityType,
                        entity_id: props.entityId ?? undefined,
                        text: comment,
                        thread_id: props.threadId ?? undefined,
                      });
                    }
                  }
                }}
                size="small"
              >
                <SendIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      }}
    />
  );
};

export const Comments = () => {
  const loc = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const [hovered, setHovered] = useState<boolean>(false);

  const [replyingTo, setReplyingTo] = useState<Comment | null>(null);
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [commentToDelete, setCommentToDelete] = useState<Comment | null>(null);

  const stackRef = useRef<HTMLDivElement>(null);
  const { getMember } = useMemberInfo();

  const [type, maybeId] = useMemo(() => {
    const pathParts = loc.pathname.split("/").filter((part) => part);
    return locArrToEntity(pathParts);
  }, [loc.pathname]);

  const query = useMemo(() => {
    const q = new URLSearchParams();

    q.append("entity_type", type);

    if (maybeId) {
      q.append("entity_id", maybeId);
    }

    return q;
  }, [type, maybeId]);

  const { data, isLoading, refetch } = useApiQuery<Comment[]>(`/comments?${query.toString()}`);

  // Read receipts functionality
  const { data: unreadCount, refetch: refetchUnreadCount } = useUnreadCommentsCount(
    type,
    maybeId ?? undefined,
  );
  const { data: readReceipts, refetch: refetchReadReceipts } = useCommentReadReceipts(
    type,
    maybeId ?? undefined,
  );

  const { mutate: markCommentsRead } = useMarkCommentsRead();
  const { mutate: markAllRead, isPending: isMarkingAllRead } = useMarkAllCommentsRead(
    type,
    maybeId ?? undefined,
  );

  // Helper to check if a comment is read
  const isCommentRead = useCallback(
    (commentId: string) => {
      return readReceipts?.some((receipt) => receipt.comment_id === commentId) ?? false;
    },
    [readReceipts],
  );

  // Mark a comment as read when clicked
  const handleCommentClick = useCallback(
    (commentId: string) => {
      if (!isCommentRead(commentId)) {
        markCommentsRead(
          { comment_ids: [commentId] },
          {
            onSuccess: () => {
              refetchReadReceipts();
              refetchUnreadCount();
            },
          },
        );
      }
    },
    [isCommentRead, markCommentsRead, refetchReadReceipts, refetchUnreadCount],
  );

  // Mark all comments as read
  const handleMarkAllRead = useCallback(() => {
    markAllRead(
      {},
      {
        onSuccess: () => {
          enqueueSnackbar("All comments marked as read", {
            variant: "success",
            anchorOrigin: {
              horizontal: "left",
              vertical: "bottom",
            },
          });
          refetchReadReceipts();
          refetchUnreadCount();
        },
        onError: () => {
          enqueueSnackbar("Failed to mark all comments as read", {
            variant: "error",
            anchorOrigin: {
              horizontal: "left",
              vertical: "bottom",
            },
          });
        },
      },
    );
  }, [markAllRead, enqueueSnackbar, refetchReadReceipts, refetchUnreadCount]);

  const { mutate: deleteComment, isPending: isDeletingComment } = useApiMutation(
    "DELETE",
    commentToDelete ? `/comments/${commentToDelete.id}` : "/comments",
    {
      onSuccess: () => {
        enqueueSnackbar("Comment deleted successfully", {
          variant: "success",
          anchorOrigin: {
            horizontal: "left",
            vertical: "bottom",
          },
        });
        setDeleteConfirmOpen(false);
        setCommentToDelete(null);
        refetch();
      },
      onError: () => {
        enqueueSnackbar("Failed to delete comment", {
          variant: "error",
          anchorOrigin: {
            horizontal: "left",
            vertical: "bottom",
          },
        });
      },
    },
  );

  const handleDeleteClick = (comment: Comment) => {
    setCommentToDelete(comment);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (commentToDelete) {
      deleteComment({});
    }
  };

  const { commentsOpen, setCommentsOpen } = useCommentsContext();

  useEffect(() => {
    if (commentsOpen && stackRef.current) {
      stackRef.current.scrollTo({
        top: stackRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [commentsOpen]);

  return (
    <>
      <Slide direction="left" in={commentsOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: {
              xs: "100vw",
              md: COMMENTS_DRAWER_WIDTH,
            },
            height: "100vh",
            backgroundColor: "background.paper",
            borderColor: "divider",
            zIndex: 1200,
            display: "flex",
            flexDirection: "column",
            boxShadow: 3,
          }}
        >
          <Toolbar />
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1 }}>
            <Typography variant="h6">Comments / {capitalize(type.replace(/_/g, " "))}</Typography>
            <Stack direction="row" spacing={1}>
              {unreadCount && unreadCount.unread_count > 0 && (
                <Tooltip title="Mark all comments as read">
                  <IconButton
                    aria-label="Mark all comments as read"
                    onClick={handleMarkAllRead}
                    disabled={isMarkingAllRead}
                    size="small"
                  >
                    <DoneAllIcon />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton aria-label="Close" onClick={() => setCommentsOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Stack>
          </Stack>
          <Divider />
          <Stack
            direction="column"
            spacing={2}
            sx={{ height: "100%", overflowY: "auto" }}
            alignItems="space-between"
            justifyContent="space-between"
          >
            {isLoading && (
              <Stack direction="row" justifyContent="center" sx={{ p: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Loading comments...
                </Typography>
              </Stack>
            )}
            {data?.length === 0 && (
              <EmptyState message="No comments yet. Be the first to add one!" sx={{ p: 2 }} />
            )}
            <Stack
              direction="column"
              spacing={1}
              divider={<Divider />}
              sx={{ width: "100%", p: 1, overflowY: "auto" }}
              ref={stackRef}
            >
              {data
                ?.filter((c) => !c.thread_id)
                .map((comment) => (
                  <Stack
                    key={comment.id}
                    direction="column"
                    spacing={1}
                    sx={{
                      width: "100%",
                      cursor: "pointer",
                      p: 1,
                      borderRadius: 1,
                      borderLeft: !isCommentRead(comment.id) ? "3px solid" : "none",
                      borderLeftColor: !isCommentRead(comment.id) ? "primary.main" : "transparent",
                      backgroundColor: !isCommentRead(comment.id) ? "action.hover" : "transparent",
                      "&:hover": {
                        backgroundColor: "action.hover",
                      },
                    }}
                    {...clickableProps(() => handleCommentClick(comment.id))}
                    onClick={() => handleCommentClick(comment.id)}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      justifyContent="space-between"
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.75}
                        justifyContent="flex-start"
                      >
                        <Avatar
                          alt="Memeber Avatar"
                          src={getMember(comment.created_by)?.imageUrl}
                          sx={{ width: 22, height: 22 }}
                        />
                        <Typography variant="body2" fontWeight={500}>
                          {memberFullName(comment.created_by, getMember(comment.created_by))}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(comment.created_at).toLocaleString()}
                      </Typography>
                    </Stack>
                    <CommentText text={comment.text} />
                    <Stack direction="row" justifyContent="flex-end" spacing={1}>
                      <Tooltip title="Edit this comment">
                        <IconButton
                          aria-label="Edit this comment"
                          size="small"
                          disabled={editingComment?.id === comment.id}
                          onClick={() => setEditingComment(comment)}
                          color="default"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete this comment">
                        <IconButton
                          aria-label="Delete this comment"
                          size="small"
                          disabled={isDeletingComment}
                          loading={isDeletingComment}
                          onClick={() => handleDeleteClick(comment)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reply to this comment">
                        <IconButton
                          aria-label="Reply to this comment"
                          size="small"
                          disabled={replyingTo?.id === comment.id}
                          onClick={() => setReplyingTo(comment)}
                          color="default"
                        >
                          <ReplyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {data.filter((c) => c.thread_id === comment.id).length > 0 && (
                      <Stack direction="column" spacing={1} sx={{ pt: 2, pb: 2 }}>
                        <Divider>
                          <Typography variant="caption" color="text.secondary">
                            Replies
                          </Typography>
                        </Divider>
                        <Stack direction="column" spacing={2} divider={<Divider />}>
                          {data
                            .filter((c) => c.thread_id === comment.id)
                            .map((reply) => (
                              <Stack
                                key={reply.id}
                                direction="row"
                                alignItems="flex-start"
                                spacing={1}
                                justifyContent="flex-start"
                                sx={{
                                  pl: 2,
                                  cursor: "pointer",
                                  p: 1,
                                  borderRadius: 1,
                                  borderLeft: !isCommentRead(reply.id) ? "3px solid" : "none",
                                  borderLeftColor: !isCommentRead(reply.id)
                                    ? "primary.main"
                                    : "transparent",
                                  backgroundColor: !isCommentRead(reply.id)
                                    ? "action.hover"
                                    : "transparent",
                                  "&:hover": {
                                    backgroundColor: "action.hover",
                                  },
                                }}
                                {...clickableProps(() => handleCommentClick(reply.id))}
                                onClick={() => handleCommentClick(reply.id)}
                              >
                                <Stack
                                  direction="column"
                                  spacing={1}
                                  sx={{ flexGrow: 1, width: "100%" }}
                                >
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={1}
                                    justifyContent="space-between"
                                  >
                                    <Stack
                                      direction="row"
                                      alignItems="center"
                                      spacing={0.75}
                                      justifyContent="flex-start"
                                    >
                                      <Avatar
                                        alt="Memeber Avatar"
                                        src={getMember(reply.created_by)?.imageUrl}
                                        sx={{ width: 22, height: 22 }}
                                      />
                                      <Typography variant="body2" fontWeight={500}>
                                        {memberFullName(
                                          reply.created_by,
                                          getMember(reply.created_by),
                                        )}
                                      </Typography>
                                    </Stack>
                                    <Typography variant="caption" color="text.secondary">
                                      {new Date(reply.created_at).toLocaleString()}
                                    </Typography>
                                  </Stack>
                                  <CommentText text={reply.text} />
                                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                                    <Tooltip title="Edit this comment">
                                      <IconButton
                                        aria-label="Edit this comment"
                                        size="small"
                                        disabled={reply?.id === comment.id}
                                        onClick={() => setEditingComment(reply)}
                                        color="default"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete this comment">
                                      <IconButton
                                        aria-label="Delete this comment"
                                        size="small"
                                        disabled={isDeletingComment}
                                        loading={isDeletingComment}
                                        onClick={() => handleDeleteClick(reply)}
                                        color="error"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Stack>
                                </Stack>
                              </Stack>
                            ))}
                        </Stack>
                      </Stack>
                    )}
                  </Stack>
                ))}
            </Stack>
            <Stack direction="column" spacing={0} sx={{ p: 1, pb: 2 }}>
              <Divider>
                <Typography variant="caption" color="text.secondary">
                  {data?.length ?? 0} comments
                </Typography>
              </Divider>
              {replyingTo && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" color="text.secondary">
                    Replying to: {replyingTo.text.substring(0, 30)}
                    {replyingTo.text.length > 30 ? "..." : ""}
                  </Typography>
                  <IconButton
                    aria-label="Cancel reply"
                    size="small"
                    onClick={() => setReplyingTo(null)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
              {editingComment && (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="subtitle2" color="text.secondary">
                    Editing
                  </Typography>
                  <IconButton
                    aria-label="Cancel editing"
                    size="small"
                    onClick={() => setEditingComment(null)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
              <CommentAdd
                entityType={type}
                entityId={maybeId ?? undefined}
                threadId={replyingTo?.id ?? undefined}
                commentId={editingComment?.id}
                snackbar
                onSuccess={() => {
                  setReplyingTo(null);
                  setEditingComment(null);

                  if (stackRef.current) {
                    stackRef.current.scrollTo({
                      top: stackRef.current.scrollHeight,
                      behavior: "smooth",
                    });
                  }

                  refetch();
                  refetchUnreadCount();
                  refetchReadReceipts();
                }}
              />
            </Stack>
          </Stack>
          <Divider />
        </Box>
      </Slide>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-comment-dialog-title"
        aria-describedby="delete-comment-dialog-description"
      >
        <DialogTitle id="delete-comment-dialog-title">Delete Comment</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-comment-dialog-description">
            Are you sure you want to delete this comment? This action cannot be undone.
            {commentToDelete && (
              <Typography variant="body2" sx={{ mt: 1, fontStyle: "italic" }}>
                {commentToDelete.text.substring(0, 50)}
                {commentToDelete.text.length > 50 ? "..." : ""}
              </Typography>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isDeletingComment}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeletingComment}
          >
            {isDeletingComment ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Stack
        direction="row"
        sx={{
          position: "fixed",
          bottom: 10,
          right: hovered ? 10 : -25,
          transition: "right 0.3s ease-in-out",
        }}
        onMouseOver={() => setHovered(true)}
        onMouseOut={() => setHovered(false)}
      >
        <Tooltip title="See contextual comments" placement="left">
          <Fab
            color="primary"
            aria-label="comments"
            size="medium"
            onClick={() => setCommentsOpen(!commentsOpen)}
          >
            <Badge
              anchorOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
              badgeContent={
                // Show unread count if there are unread comments
                unreadCount && unreadCount.unread_count > 0
                  ? unreadCount.unread_count
                  : // Show dot (empty badge) if all are read but there are comments
                    unreadCount && unreadCount.total_count > 0
                    ? ""
                    : // Show nothing if no comments at all
                      undefined
              }
              color="secondary"
              variant={unreadCount && unreadCount.unread_count > 0 ? "standard" : "dot"}
            >
              <ChatIcon fontSize="small" />
            </Badge>
          </Fab>
        </Tooltip>
      </Stack>
    </>
  );
};
